import { useCallback, useEffect, useRef, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { toast } from "sonner";
import { CheckCircle2, Link2, Loader2, Lock, Pencil, Plus, StickyNote, Trash2, Unlink, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAccountsHead } from "@/components/GlobalSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  createBankAccount, deleteStatement, getRecon, lockReconMonth, getReconOverview, listBankAccounts, previewStatement,
  reconMatch, reconNote, reconUnmatch, updateBankAccount, uploadStatement,
} from "@/lib/api";
import { Empty, Stat, TH, TR, errMsg, money } from "@/components/accounts/shared";

const KIND_LABEL = {
  auto_ref: "Name / ref match", auto_exact: "Same day", auto_window: "Within 3 days", gateway: "Gateway settlement",
  cash: "Cash deposit", manual: "Manual",
};
const FIELDS = [
  ["date", "Date", true], ["description", "Narration / description"], ["reference", "Reference / UTR"],
  ["credit", "Credit (money in)"], ["debit", "Debit (money out)"], ["amount", "Signed amount (if no credit/debit)"], ["balance", "Balance"],
];

export default function BankRecon({ month, country, gym, gyms }) {
  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);           // account being added / edited
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([getReconOverview(month), listBankAccounts()]);
      setOverview(o);
      setAccounts(a);
    } catch (e) {
      toast.error(errMsg(e, "Could not load reconciliation"));
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const rows = (overview || []).filter((r) => (!country || r.country === country) && (!gym || r.gyms.some((g) => g.id === gym)));

  if (!overview) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-5" data-testid="accounts-recon">
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-zinc-100">Bank accounts · month tally</div>
            <div className="text-[11px] text-zinc-500">FPA total = money in FPA expects in this account (gateway payments net of fees) less refunds and payouts. Must reach 0, or every difference explained.</div>
          </div>
          <Button size="sm" onClick={() => setEditing({ name: "", bank: "", last4: "", entity: "", facility_ids: [], gateway_modes: null, cash_modes: null })}
            className="bg-blue-600 hover:bg-blue-500 h-8" data-testid="recon-add-account"><Plus className="h-3.5 w-3.5 mr-1" /> Bank account</Button>
        </div>
        {rows.length === 0 ? (
          <Empty>No bank accounts yet. Add each account (bank, last 4 digits, the gyms paid into it), then upload its statement.</Empty>
        ) : (
          <table className="w-full text-sm min-w-[880px]">
            <thead><tr className={TH}>
              <th className="px-3 py-2 font-medium">Account</th><th className="px-3 py-2 font-medium">Gyms</th>
              <th className="px-3 py-2 font-medium text-right">FPA total</th><th className="px-3 py-2 font-medium text-right">Bank (credits − debits)</th>
              <th className="px-3 py-2 font-medium text-right">Difference</th><th className="px-3 py-2 font-medium text-right">Unexplained</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.account_id} className={`${TR} cursor-pointer ${selected === r.account_id ? "bg-[#161616]" : ""}`}
                  onClick={() => setSelected(r.account_id)} data-testid={`recon-account-${r.account_id}`}>
                  <td className="px-3 py-2"><div className="text-zinc-200 font-medium">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">{[r.bank, r.last4 && `••${r.last4}`, r.entity, r.currency_code].filter(Boolean).join(" · ")}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{r.gyms.map((g) => g.name).join(", ")}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r, r.fpa_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.has_statement ? money(r, r.bank_total) : <span className="text-zinc-600">no statement</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.has_statement ? money(r, r.difference) : "—"}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${Math.abs(r.unexplained) >= 1 ? "text-rose-300" : "text-emerald-300"}`}>{r.has_statement ? money(r, r.unexplained) : "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {!r.has_statement ? <span className="text-zinc-500">Upload statement</span>
                      : r.reconciled ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Reconciled</span>
                        : <span className="text-amber-300">{r.fpa_only} FPA-only · {r.bank_only} bank-only</span>}
                    {r.locked && <span className="ml-2 text-[10px] text-zinc-500">locked</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <AccountDetail key={`${selected}:${month}`} accountId={selected} month={month} onChanged={load}
        onEdit={() => setEditing(accounts.find((a) => a.id === selected))} />}

      {editing && <AccountDialog account={editing} gyms={gyms} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AccountDialog({ account, gyms, onClose, onSaved }) {
  const [f, setF] = useState({
    ...account,
    gateway_text: account.gateway_modes ? account.gateway_modes.join(", ") : "",
    cash_text: account.cash_modes ? account.cash_modes.join(", ") : "",
  });
  const [busy, setBusy] = useState(false);
  const toggleGym = (id) => setF((x) => ({ ...x, facility_ids: x.facility_ids.includes(id) ? x.facility_ids.filter((g) => g !== id) : [...x.facility_ids, id] }));
  const list = (t) => t.split(",").map((s) => s.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    const payload = {
      name: f.name, bank: f.bank, last4: f.last4, entity: f.entity, facility_ids: f.facility_ids,
      gateway_modes: f.gateway_text.trim() ? list(f.gateway_text) : (account.id ? [] : null),
      cash_modes: f.cash_text.trim() ? list(f.cash_text) : (account.id ? [] : null),
    };
    try {
      if (account.id) await updateBankAccount(account.id, payload);
      else await createBankAccount(payload);
      toast.success("Bank account saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Could not save the account"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg">
        <DialogHeader><DialogTitle>{account.id ? "Edit bank account" : "Add bank account"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2 space-y-1"><span className="text-xs text-zinc-400">Name</span>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Bangalore gyms – current account" className="bg-[#0a0a0a] border-[#262626]" data-testid="recon-acct-name" /></label>
            <label className="space-y-1"><span className="text-xs text-zinc-400">Bank</span>
              <Input value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
            <label className="space-y-1"><span className="text-xs text-zinc-400">Last 4 digits</span>
              <Input value={f.last4} maxLength={4} inputMode="numeric" onChange={(e) => setF({ ...f, last4: e.target.value.replace(/\D/g, "") })} className="bg-[#0a0a0a] border-[#262626]" /></label>
            <label className="col-span-2 space-y-1"><span className="text-xs text-zinc-400">Legal entity</span>
              <Input value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
          </div>
          <div>
            <div className="text-xs text-zinc-400 mb-1">Gyms paid into this account (same currency)</div>
            <div className="flex flex-wrap gap-1.5">
              {gyms.map((g) => (
                <button key={g.id} type="button" onClick={() => toggleGym(g.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${f.facility_ids.includes(g.id) ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>
                  {g.name} <span className="text-zinc-500">{g.currency_code}</span>
                </button>
              ))}
            </div>
          </div>
          <label className="block space-y-1"><span className="text-xs text-zinc-400">Gateway modes (settled in one credit, net of fees)</span>
            <Input value={f.gateway_text} onChange={(e) => setF({ ...f, gateway_text: e.target.value })} placeholder={account.id ? "" : "Blank = modes with a fee in Settings"} className="bg-[#0a0a0a] border-[#262626]" /></label>
          <label className="block space-y-1"><span className="text-xs text-zinc-400">Cash modes (banked as cash deposits)</span>
            <Input value={f.cash_text} onChange={(e) => setF({ ...f, cash_text: e.target.value })} placeholder={account.id ? "" : "Blank = Cash"} className="bg-[#0a0a0a] border-[#262626]" /></label>
          <p className="text-[11px] text-zinc-500">Only the last four digits are stored, never the full account number.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.name.trim() || !f.facility_ids.length} className="bg-blue-600 hover:bg-blue-500" data-testid="recon-acct-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountDetail({ accountId, month, onChanged, onEdit }) {
  const askDelete = useDeleteAsk();
  const { user } = useAuth();
  const head = isAccountsHead(user);
  const [rec, setRec] = useState(null);
  const [pick, setPick] = useState({ lines: [], items: [] });
  const [mapping, setMapping] = useState(null);           // {file, headers, mapping, sample}
  const [noteFor, setNoteFor] = useState(null);           // {line_ids|item_keys, note, label}
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setRec(await getRecon(accountId, month)); } catch (e) { toast.error(errMsg(e, "Could not load the account")); }
  }, [accountId, month]);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => { await load(); onChanged(); setPick({ lines: [], items: [] }); };

  const onFile = async (file) => {
    if (!file) return;
    try {
      const p = await previewStatement(accountId, file);
      if (p.saved_mapping_fits) {               // same bank format as last time: one click
        const s = await uploadStatement(accountId, file, p.mapping);
        toast.success(`${s.rows} lines imported${s.skipped ? `, ${s.skipped} already uploaded` : ""}`);
        refresh();
      } else {
        setMapping({ file, ...p });
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not read that file"));
    } finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const importMapped = async () => {
    try {
      const s = await uploadStatement(accountId, mapping.file, mapping.mapping);
      toast.success(`${s.rows} lines imported${s.skipped ? `, ${s.skipped} already uploaded` : ""}. Column mapping saved for next time.`);
      setMapping(null);
      refresh();
    } catch (e) { toast.error(errMsg(e, "Import failed")); }
  };
  const matchPicked = async () => {
    try {
      await reconMatch({ account_id: accountId, line_ids: pick.lines, item_keys: pick.items, note: "" });
      toast.success("Matched");
      refresh();
    } catch (e) { toast.error(errMsg(e, "Could not match")); }
  };
  const unmatch = async (m) => {
    try { await reconUnmatch({ account_id: accountId, line_ids: m.line_ids, item_keys: m.item_keys }); refresh(); }
    catch (e) { toast.error(errMsg(e, "Could not unmatch")); }
  };
  const saveNote = async () => {
    try {
      await reconNote({ account_id: accountId, line_ids: noteFor.line_ids || [], item_keys: noteFor.item_keys || [], note: noteFor.note });
      setNoteFor(null);
      refresh();
    } catch (e) { toast.error(errMsg(e, "Could not save the note")); }
  };
  const lock = async () => {
    let note = "";
    if (!rec.tally.reconciled) {
      note = window.prompt(`Not reconciled: ${money(rec.account, rec.tally.unexplained, true)} unexplained${rec.has_statement ? "" : " and no bank statement"}. `
        + "Close the month anyway? Write the reason (kept in the audit log with the unexplained amount):") || "";
      if (!note.trim()) return;
    }
    try {
      await lockReconMonth({ account_id: accountId, month, note, force: !rec.tally.reconciled });
      toast.success(`${month} closed for ${rec.account.gyms.map((g) => g.name).join(", ")}`);
      refresh();
    } catch (e) { toast.error(errMsg(e, "Could not lock the month")); }
  };
  const togglePick = (kind, id) => setPick((p) => ({ ...p, [kind]: p[kind].includes(id) ? p[kind].filter((x) => x !== id) : [...p[kind], id] }));

  if (!rec) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const a = rec.account;
  const t = rec.tally;

  return (
    <div className="space-y-4" data-testid="recon-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-base font-semibold text-zinc-100">{a.name} <button onClick={onEdit} className="text-zinc-500 hover:text-zinc-300 ml-1" aria-label="Edit account"><Pencil className="h-3.5 w-3.5 inline" /></button></div>
          <div className="text-xs text-zinc-500">{a.gyms.map((g) => g.name).join(", ")} · gateway: {(a.gateway_modes || []).join(", ") || "none"} · cash: {(a.cash_modes || []).join(", ") || "none"}</div>
        </div>
        <div className="flex items-center gap-2">
          {rec.locked ? <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> {month} closed</span>
            : (t.reconciled || head) && (
              <Button size="sm" variant="outline" onClick={lock} className={`h-8 bg-transparent ${t.reconciled ? "border-emerald-500/50 text-emerald-200" : "border-[#262626] text-zinc-400"}`} data-testid="recon-lock">
                <Lock className="h-3.5 w-3.5 mr-1.5" /> {t.reconciled ? "Mark reconciled & close month" : "Close month anyway"}</Button>
            )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="recon-file" />
          <Button size="sm" onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid="recon-upload">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload statement (CSV / XLSX)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <Stat label="FPA total" value={money(a, t.fpa_total)} sub="expected in the bank" />
        <Stat label="Bank" value={rec.has_statement ? money(a, t.bank_total) : "—"} sub={rec.has_statement ? "credits − debits" : "no statement for this month"} />
        <Stat label="Difference" value={money(a, t.difference)} sub="bank − FPA" />
        <Stat label="Explained" value={money(a, t.explained)} sub={`notes · ${money(a, t.cash_in_hand)} cash in hand`} />
        <Stat label="Unexplained" value={money(a, t.unexplained)} tone={t.reconciled ? "good" : "bad"} sub={t.reconciled ? "Reconciled" : "must reach 0"} />
      </div>

      {(pick.lines.length > 0 || pick.items.length > 0) && (
        <div className="sticky top-16 z-10 card-surface px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-zinc-300">{pick.lines.length} bank line(s) · {pick.items.length} FPA payment(s) selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setPick({ lines: [], items: [] })}>Clear</Button>
            <Button size="sm" disabled={!pick.lines.length || !pick.items.length} onClick={matchPicked} className="bg-blue-600 hover:bg-blue-500" data-testid="recon-match-picked">
              <Link2 className="h-3.5 w-3.5 mr-1" /> Match selected</Button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="In FPA, not in the bank" hint="Recorded in FPA but no bank line found (amount within ±3 days)." count={rec.fpa_only.length}>
          {rec.fpa_only.length === 0 ? <Empty>Every FPA payment found its bank line.</Empty> : rec.fpa_only.map((it) => (
            <ItemRow key={it.key} it={it} acct={a} checked={pick.items.includes(it.key)} onCheck={() => togglePick("items", it.key)}
              onNote={() => setNoteFor({ item_keys: [it.key], note: it.note?.note || "", label: `${it.member} · ${money(a, it.expected)}` })} />
          ))}
        </Section>
        <Section title="In the bank, not in FPA" hint="Bank lines with no FPA payment. Explain each (interest, owner top-up, transfer…) or match it." count={rec.bank_only.length}>
          {rec.bank_only.length === 0 ? <Empty>{rec.has_statement ? "Every bank line is accounted for." : "Upload this month's statement."}</Empty> : rec.bank_only.map((ln) => (
            <LineRow key={ln.id} ln={ln} acct={a} checked={pick.lines.includes(ln.id)} onCheck={() => togglePick("lines", ln.id)}
              onNote={() => setNoteFor({ line_ids: [ln.id], note: ln.note?.note || "", label: `${ln.description} · ${money(a, ln.credit || -ln.debit)}` })} />
          ))}
        </Section>
      </div>

      {rec.cash_in_hand.length > 0 && (
        <Section title="Cash in hand" hint="Cash not yet seen in a deposit (deposits are matched to cash taken up to 14 days before them, see Settings). To match by hand, tick the payments and the deposit line, then Match selected." count={rec.cash_in_hand.length}>
          {rec.cash_in_hand.map((it) => <ItemRow key={it.key} it={it} acct={a} checked={pick.items.includes(it.key)} onCheck={() => togglePick("items", it.key)} />)}
        </Section>
      )}

      <Section title="Matched" hint="Automatic matches are recalculated each time; unmatching one stops it coming back." count={rec.matches.length}>
        {rec.matches.length === 0 ? <Empty>No matches yet.</Empty> : (
          <table className="w-full text-sm">
            <tbody>
              {rec.matches.map((m) => (
                <tr key={m.id} className={TR}>
                  <td className="px-3 py-2 align-top w-1/2">
                    {m.lines.map((ln) => <div key={ln.id} className="text-zinc-300"><span className="tabular-nums text-zinc-500 mr-2">{ln.date}</span>{ln.description || ln.reference}</div>)}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-zinc-400">
                    {m.items.map((it) => <div key={it.key}>{it.member} · {it.mode} · {money(a, it.amount)}{it.fee ? ` (fee ${money(a, it.fee, true)})` : ""}</div>)}
                    {m.note && <div className="text-zinc-500 italic">{m.note}</div>}
                  </td>
                  <td className="px-3 py-2 align-top text-right tabular-nums whitespace-nowrap">
                    {money(a, m.bank_amount, true)}
                    {Math.abs(m.difference) >= 0.01 && <div className="text-[10px] text-amber-300">diff {money(a, m.difference, true)}</div>}
                    <div className="text-[10px] text-zinc-500">{KIND_LABEL[m.kind] || m.kind}</div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <button onClick={() => unmatch(m)} title="Unmatch" className="text-zinc-500 hover:text-rose-300"><Unlink className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Statements uploaded" count={rec.statements.length}>
        {rec.statements.length === 0 ? <Empty>None yet.</Empty> : rec.statements.map((s) => (
          <div key={s.id} className="px-3 py-2 flex items-center justify-between text-sm border-b border-[#1a1a1a] last:border-0">
            <span className="text-zinc-300">{s.filename} <span className="text-zinc-500 text-xs">{s.from} → {s.to} · {s.rows} lines · {s.uploaded_by}</span></span>
            <button onClick={async () => {
              const reason = askDelete("this statement", "Remove this statement and its lines?");
              if (reason === null) return;
              try { if (!deletionRequested(await deleteStatement(s.id, reason))) refresh(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove it"); }
            }}
              className="text-zinc-500 hover:text-rose-300" aria-label="Remove statement"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </Section>

      {mapping && (
        <Dialog open onOpenChange={(o) => !o && setMapping(null)}>
          <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl">
            <DialogHeader><DialogTitle>Match the statement's columns</DialogTitle></DialogHeader>
            <p className="text-xs text-zinc-500">Saved for this account, so the next upload in this format is one click.</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {FIELDS.map(([k, label, req]) => (
                <label key={k} className="space-y-1">
                  <span className="text-xs text-zinc-400">{label}{req ? " *" : ""}</span>
                  <select value={mapping.mapping[k] || ""} onChange={(e) => setMapping({ ...mapping, mapping: { ...mapping.mapping, [k]: e.target.value || undefined } })}
                    className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm" data-testid={`recon-map-${k}`}>
                    <option value="">—</option>
                    {mapping.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="text-xs text-zinc-500 mt-2">First rows of the file:</div>
            <div className="overflow-x-auto max-h-40 text-[11px] text-zinc-400 border border-[#1f1f1f] rounded">
              <table><tbody>{mapping.preview.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-2 py-0.5 whitespace-nowrap">{c}</td>)}</tr>)}</tbody></table>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setMapping(null)}>Cancel</Button>
              <Button onClick={importMapped} disabled={!mapping.mapping.date} className="bg-blue-600 hover:bg-blue-500" data-testid="recon-import">Import</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {noteFor && (
        <Dialog open onOpenChange={(o) => !o && setNoteFor(null)}>
          <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
            <DialogHeader><DialogTitle>Explain this difference</DialogTitle></DialogHeader>
            <div className="text-xs text-zinc-500">{noteFor.label}</div>
            <Input autoFocus value={noteFor.note} onChange={(e) => setNoteFor({ ...noteFor, note: e.target.value })}
              placeholder="e.g. Bank interest · Owner top-up · Paid into the wrong account" className="bg-[#0a0a0a] border-[#262626]" data-testid="recon-note-input" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setNoteFor(null)}>Cancel</Button>
              <Button onClick={saveNote} className="bg-blue-600 hover:bg-blue-500" data-testid="recon-note-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Section({ title, hint, count, children }) {
  return (
    <div className="card-surface overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#1a1a1a]">
        <div className="text-sm font-semibold text-zinc-100">{title} {count != null && <span className="text-zinc-500 font-normal">({count})</span>}</div>
        {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
      </div>
      <div className="max-h-[420px] overflow-y-auto">{children}</div>
    </div>
  );
}

function ItemRow({ it, acct, checked, onCheck, onNote }) {
  return (
    <label className="px-3 py-2 flex items-start gap-2 text-sm border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onCheck} className="mt-1 accent-blue-500" />
      <div className="flex-1 min-w-0">
        <div className="text-zinc-200 truncate">{it.member || it.kind} <span className="text-[10px] text-zinc-500">{it.gym}</span></div>
        <div className="text-[11px] text-zinc-500">{it.date} · {it.mode || "no mode"} · {it.kind}{it.programme ? ` · ${it.programme}` : ""}</div>
        {it.note && <div className="text-[11px] text-amber-200/80 italic">{it.note.note}</div>}
      </div>
      <div className="text-right tabular-nums">
        <div className={it.amount < 0 ? "text-rose-200" : "text-zinc-200"}>{money(acct, it.expected, true)}</div>
        {it.fee > 0 && <div className="text-[10px] text-zinc-500">{money(acct, it.amount)} − fee</div>}
      </div>
      {onNote && <button type="button" onClick={(e) => { e.preventDefault(); onNote(); }} title="Explain" className="text-zinc-500 hover:text-amber-300 mt-0.5"><StickyNote className="h-3.5 w-3.5" /></button>}
    </label>
  );
}

function LineRow({ ln, acct, checked, onCheck, onNote }) {
  const amt = (ln.credit || 0) - (ln.debit || 0);
  return (
    <label className="px-3 py-2 flex items-start gap-2 text-sm border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onCheck} className="mt-1 accent-blue-500" />
      <div className="flex-1 min-w-0">
        <div className="text-zinc-200 truncate">{ln.description || "(no narration)"}</div>
        <div className="text-[11px] text-zinc-500">{ln.date}{ln.reference ? ` · ${ln.reference}` : ""}</div>
        {ln.note && <div className="text-[11px] text-amber-200/80 italic">{ln.note.note}</div>}
      </div>
      <div className={`text-right tabular-nums ${amt < 0 ? "text-rose-200" : "text-emerald-200"}`}>{money(acct, amt, true)}</div>
      <button type="button" onClick={(e) => { e.preventDefault(); onNote(); }} title="Explain" className="text-zinc-500 hover:text-amber-300 mt-0.5"><StickyNote className="h-3.5 w-3.5" /></button>
    </label>
  );
}
